import { CalendarRange, Download, RefreshCw, Ruler, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { LineChart } from '@/components/charts/line-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { formatDateTimeValue } from '@/lib/utils';

import { MonitoreoNav } from '../components/monitoreo-nav';
import { galones, medida, mensajeControlador } from '../lib/monitoreo-live';
import { aApi, aLocal } from '../lib/pts2-format';
import { useControllerSetup, useMedicionesTanque } from '../monitoreo.api';
import type { FiltrosRango } from '../monitoreo.types';

const SELECT_CLASSES =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Rango por defecto: las últimas 24 horas del día en curso. */
function rangoInicial(): FiltrosRango {
  const ahora = new Date();
  const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 0, 0);

  return { tank: '', fecha_inicio: aLocal(inicio), fecha_fin: aLocal(ahora) };
}

const COLUMNAS_MEDICION =
  'grid grid-cols-[3.5rem_11rem_8rem_8rem_8rem_9rem_9rem_9.5rem_9.5rem_9rem_8.5rem] items-center gap-x-3';
const COLUMNAS_ENTREGA =
  'grid grid-cols-[3.5rem_11rem_11rem_9rem_9rem_9.5rem_9.5rem_9.5rem_9.5rem] items-center gap-x-3';

/**
 * Medición de tanques (`/monitoreo/medicion-tanques`).
 *
 * Puerto de `Reportes::medicion_tanques()`: los cambios de nivel del tanque en
 * el período (mediciones de la sonda) y, aparte, las entregas de producto
 * (descargas) con el volumen recibido calculado por el controlador.
 */
export function MedicionTanquesPage() {
  useDocumentTitle('Monitoreo · Medición de Tanques Controlador');

  const [filtros, setFiltros] = useState<FiltrosRango>(() => rangoInicial());

  const setup = useControllerSetup();
  const reporte = useMedicionesTanque(filtros);

  const tanques = setup.data?.tanques ?? [];
  const datos = reporte.data;
  const mediciones = datos?.measurements ?? [];
  const entregas = datos?.deliveries ?? [];

  const tanque = tanques.find((item) => String(item.id_tank) === filtros.tank) ?? null;

  /** Serie del volumen de producto a lo largo del período. */
  const grafico = useMemo(() => {
    const filas = datos?.measurements ?? [];

    return {
      labels: filas.map((fila) => formatDateTimeValue(fila.DateTime)),
      series: [
        {
          label: 'Volumen de producto',
          data: filas.map((fila) => fila.ProductVolume),
          color: tanque?.color ?? '#6366f1',
        },
      ],
    };
  }, [datos, tanque]);

  const exportarMediciones = () => {
    if (mediciones.length === 0) return;

    exportarCsv(
      `mediciones-tanque-${filtros.tank}`,
      [
        'Fecha y hora',
        'Alto de producto (mm)',
        'Alto de agua (mm)',
        'Temperatura (°C)',
        'Volumen de producto (gal)',
        'Volumen de agua (gal)',
        'Disponibilidad (gal)',
        'Volumen de producto TC (gal)',
        'Densidad (kg/m³)',
        'Masa (kg)',
      ],
      mediciones.map((fila) => [
        fila.DateTime ?? '',
        fila.ProductHeight.toFixed(2),
        fila.WaterHeight.toFixed(2),
        fila.Temperature.toFixed(2),
        fila.ProductVolume.toFixed(3),
        fila.WaterVolume.toFixed(3),
        fila.ProductUllage.toFixed(3),
        fila.ProductTCVolume.toFixed(3),
        fila.ProductDensity.toFixed(2),
        fila.ProductMass.toFixed(2),
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Ruler className="size-6 text-brand-500" aria-hidden="true" />
            Medición de Tanques Controlador
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Cambios de nivel registrados por la sonda del tanque y entregas de producto recibidas
            en el período.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void reporte.refetch()}
            loading={reporte.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportarMediciones}
            disabled={mediciones.length === 0}
          >
            <Download className="size-3.5" aria-hidden="true" />
            Exportar
          </Button>
        </div>
      </header>

      <MonitoreoNav active="medicion" />

      <SectionCard title="Filtros" description="Tanque y rango de fechas a consultar.">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-[12rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Tanque
            <Select
              value={filtros.tank}
              onChange={(event) => setFiltros((prev) => ({ ...prev, tank: event.target.value }))}
              className={SELECT_CLASSES}
            >
              <option value="">Selecciona un tanque</option>
              {tanques.map((item) => (
                <option key={item.id_tank} value={String(item.id_tank)}>
                  Tanque {item.id_tank} · {item.producto}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex min-w-[13rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Desde
            <CampoFecha
             
              value={filtros.fecha_inicio}
              onChange={(event) =>
                setFiltros((prev) => ({ ...prev, fecha_inicio: event.target.value }))
              }
              className={SELECT_CLASSES}
             tipo="datetime-local" />
          </label>

          <label className="flex min-w-[13rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Hasta
            <CampoFecha
             
              value={filtros.fecha_fin}
              onChange={(event) => setFiltros((prev) => ({ ...prev, fecha_fin: event.target.value }))}
              className={SELECT_CLASSES}
             tipo="datetime-local" />
          </label>

          <Button
            size="sm"
            onClick={() => void reporte.refetch()}
            loading={reporte.isFetching}
            className="mb-px"
          >
            <Search className="size-3.5" aria-hidden="true" />
            Consultar
          </Button>

          <p className="mb-2 flex items-center gap-1.5 text-xs text-ink-500">
            <CalendarRange className="size-3.5" aria-hidden="true" />
            {aApi(filtros.fecha_inicio) || 'sin fecha'} → {aApi(filtros.fecha_fin) || 'sin fecha'}
          </p>
        </div>
      </SectionCard>

      {!filtros.tank ? (
        <Alert variant="info" title="Selecciona un tanque">
          Elige el tanque y el rango de fechas para ver sus mediciones y entregas.
        </Alert>
      ) : reporte.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-72 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
        </div>
      ) : reporte.isError ? (
        <Alert variant="error" title="No se pudieron consultar las mediciones">
          <div className="flex flex-wrap items-center gap-3">
            <span>{mensajeControlador(reporte.error)}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void reporte.refetch()}
              loading={reporte.isFetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard
              title="Cambios de nivel del tanque"
              description="Volumen de producto medido por la sonda."
            >
              {mediciones.length === 0 ? (
                <p className="py-16 text-center text-sm text-ink-500">
                  El controlador no devolvió mediciones en el período.
                </p>
              ) : (
                <LineChart
                  labels={grafico.labels}
                  series={grafico.series}
                  height={260}
                  spanGaps
                  showLegend={false}
                />
              )}
            </SectionCard>

            <SectionCard
              title="Última medición"
              description="Valores actuales del tanque seleccionado."
              action={
                <Badge variant="outline">
                  {mediciones.length} medición(es)
                </Badge>
              }
            >
              {mediciones.length === 0 ? (
                <p className="py-16 text-center text-sm text-ink-500">Sin mediciones.</p>
              ) : (
                (() => {
                  const ultima = mediciones[mediciones.length - 1];

                  if (!ultima) return null;

                  return (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Fecha y hora
                        </dt>
                        <dd className="text-ink-900">{formatDateTimeValue(ultima.DateTime)}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Producto
                        </dt>
                        <dd className="text-ink-900">
                          {ultima.FuelGradeName ?? tanque?.producto ?? '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Volumen de producto
                        </dt>
                        <dd className="text-ink-900">{galones(ultima.ProductVolume)} gal</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Alto de producto
                        </dt>
                        <dd className="text-ink-900">{medida(ultima.ProductHeight)} mm</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Volumen de agua
                        </dt>
                        <dd className="text-ink-900">{galones(ultima.WaterVolume)} gal</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Temperatura
                        </dt>
                        <dd className="text-ink-900">{medida(ultima.Temperature)} °C</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Disponibilidad
                        </dt>
                        <dd className="text-ink-900">{galones(ultima.ProductUllage)} gal</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                          Densidad
                        </dt>
                        <dd className="text-ink-900">{medida(ultima.ProductDensity)} kg/m³</dd>
                      </div>
                    </dl>
                  );
                })()
              )}
            </SectionCard>
          </div>

          <SectionCard
            title="Mediciones del período"
            description="Una fila por cada lectura de la sonda."
            action={<Badge variant="outline">{mediciones.length} fila(s)</Badge>}
          >
            <div className="max-h-[28rem] overflow-auto">
              <div className="min-w-[92rem]">
                <div
                  className={`${COLUMNAS_MEDICION} sticky top-0 border-b border-ink-100 bg-white pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400`}
                >
                  <span>#</span>
                  <span>Fecha y hora</span>
                  <span className="text-right">Alto producto mm</span>
                  <span className="text-right">Alto agua mm</span>
                  <span className="text-right">Temperatura °C</span>
                  <span className="text-right">Volumen producto gal</span>
                  <span className="text-right">Volumen agua gal</span>
                  <span className="text-right">Disponibilidad gal</span>
                  {/* El panel anterior repetía `ProductVolume` en esta columna. */}
                  <span className="text-right">Volumen producto TC gal</span>
                  <span className="text-right">Densidad kg/m³</span>
                  <span className="text-right">Masa kg</span>
                </div>

                {mediciones.map((fila, indice) => (
                  <div
                    key={`${fila.DateTime}-${indice}`}
                    className={`${COLUMNAS_MEDICION} border-b border-ink-50 py-1.5 text-sm text-ink-800`}
                  >
                    <span className="tabular-nums text-ink-500">{indice + 1}</span>
                    <span className="tabular-nums">{formatDateTimeValue(fila.DateTime)}</span>
                    <span className="text-right tabular-nums">{medida(fila.ProductHeight)}</span>
                    <span className="text-right tabular-nums">{medida(fila.WaterHeight)}</span>
                    <span className="text-right tabular-nums">{medida(fila.Temperature)}</span>
                    <span className="text-right tabular-nums">{galones(fila.ProductVolume)}</span>
                    <span className="text-right tabular-nums">{galones(fila.WaterVolume)}</span>
                    <span className="text-right tabular-nums">{galones(fila.ProductUllage)}</span>
                    <span className="text-right tabular-nums">{galones(fila.ProductTCVolume)}</span>
                    <span className="text-right tabular-nums">{medida(fila.ProductDensity)}</span>
                    <span className="text-right tabular-nums">{medida(fila.ProductMass)}</span>
                  </div>
                ))}

                {mediciones.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-500">
                    Sin mediciones en el período.
                  </p>
                ) : null}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Entregas en tanque"
            description="Descargas de producto detectadas por el controlador."
            action={<Badge variant="outline">{entregas.length} entrega(s)</Badge>}
          >
            <div className="max-h-[24rem] overflow-auto">
              <div className="min-w-[88rem]">
                <div
                  className={`${COLUMNAS_ENTREGA} sticky top-0 border-b border-ink-100 bg-white pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400`}
                >
                  <span>#</span>
                  <span>Inicio</span>
                  <span>Fin</span>
                  <span className="text-right">Alto inicio mm</span>
                  <span className="text-right">Alto final mm</span>
                  <span className="text-right">Volumen inicio gal</span>
                  <span className="text-right">Volumen final gal</span>
                  <span className="text-right">Dispensado gal</span>
                  <span className="text-right">Recibido gal</span>
                </div>

                {entregas.map((fila, indice) => (
                  <div
                    key={`${fila.DateTimeStart}-${indice}`}
                    className={`${COLUMNAS_ENTREGA} border-b border-ink-50 py-1.5 text-sm text-ink-800`}
                  >
                    <span className="tabular-nums text-ink-500">{indice + 1}</span>
                    <span className="tabular-nums">{formatDateTimeValue(fila.DateTimeStart)}</span>
                    <span className="tabular-nums">{formatDateTimeValue(fila.DateTimeEnd)}</span>
                    <span className="text-right tabular-nums">
                      {medida(fila.ProductHeightStart)}
                    </span>
                    <span className="text-right tabular-nums">{medida(fila.ProductHeightEnd)}</span>
                    <span className="text-right tabular-nums">
                      {galones(fila.ProductVolumeStart)}
                    </span>
                    <span className="text-right tabular-nums">{galones(fila.ProductVolumeEnd)}</span>
                    <span className="text-right tabular-nums">
                      {galones(fila.ProductVolumeDispensed)}
                    </span>
                    <span className="text-right font-semibold tabular-nums">
                      {galones(fila.ProductVolumeReceived)}
                    </span>
                  </div>
                ))}

                {entregas.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-500">
                    El controlador no reportó entregas en el período.
                  </p>
                ) : null}
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
